import { DateTimeFormatPipe } from '@/extensions/dateTimePipe';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { AppConfigService } from '@/services/app-config.service';
import { NotificationCheckService } from '@/services/notification-check.service';
import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Document, DropBoxDocument, RealPropertySummary, VaultService } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { FileSizePipe } from '../../extensions/fileSizePipe';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DropboxValidateDialogComponent } from '../dropbox-validate-dialog/dropbox-validate-dialog.component';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { ActionService } from '@/services/action.service';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-dropbox-doc-table',
  templateUrl: './dropbox-doc-table.component.html',
  styleUrls: ['./dropbox-doc-table.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgTemplateOutlet,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    DateTimeFormatPipe,
    FileSizePipe,
    TooltipDirective
  ]
})
export class DropboxDocTableComponent implements OnInit, OnDestroy {
  @Input() prop: RealPropertySummary;
  @ViewChild(MatSort) sort: MatSort;
  @Output() dataLoaded = new EventEmitter<boolean>();
  @Output() docProceeded = new EventEmitter<boolean>();

  dataSource: MatTableDataSource<Document>;
  displayedColumns = ['icon', 'name', 'sender', 'size', 'date', 'actions'];

  public hasData = false;
  sub = new Subscription();
  deleteSub = new Subscription();
  tokenSub = new Subscription();

  constructor(
    private vaultService: VaultService,
    public propHelper: PropertyHelper,
    public userHelper: UserHelper,
    private dialog: MatDialog,
    private alertService: AlertService,
    private notificationCheckService: NotificationCheckService,
    @Inject(LOCALE_ID) public locale: string,
    private appConfigService: AppConfigService,
    private actionService: ActionService
  ) {}

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.deleteSub.unsubscribe();
    this.tokenSub.unsubscribe();
  }

  loadData() {
    let filter: string;
    if (this.dataSource && this.dataSource.filter) {
      filter = this.dataSource.filter;
    }
    this.sub = this.vaultService.getRealPropertyDropBoxDocuments(this.prop.uid).subscribe((data) => {
      // building datasource with displayed data (not surce data)
      const displayedData = [];
      this.hasData = data.length > 0;
      data.forEach((i) => {
        const row = {
          uid: i.uid,
          spam: i.spam,
          icon: this.propHelper.getFileIconFromMimeType(i.mimeType),
          iconTooltip: this.propHelper.getFileTooltipFromMimeType(i.mimeType),
          name: i.name,
          size: i.size,
          filename: this.propHelper.getDocumentFilename(i),
          date: i.dateCreate,
          sender: i.senderName + ' ' + i.senderMail,
          description: i.description,
          doc: i,
          url: this.propHelper.getDropBoxDocumentUrl(i),
          viewable: this.propHelper.isDocumentViewable(i)
        };
        displayedData.push(row);
      });
      this.dataLoaded.emit(true);

      this.dataSource = new MatTableDataSource(displayedData);
      // *ngIf break the sort : must be set in background
      setTimeout(() => (this.dataSource.sort = this.sort));
      if (filter) {
        this.applyFilter(filter);
      }
    });
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  confirmDelete(doc: DropBoxDocument) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: 'Confirmez-vous la suppression du document ?'
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.doDelete(doc);
      }
    });
  }

  doDelete(doc: DropBoxDocument) {
    this.deleteSub = this.vaultService.deleteDropBoxDocument(doc.uid).subscribe(() => {
      this.alertService.success('Document supprimé');
      this.loadData();
      this.notificationCheckService.loadData();
    });
  }

  confirmValidate(doc: DropBoxDocument) {
    const dialogRef = this.dialog.open(DropboxValidateDialogComponent, {
      data: { doc, prop: this.prop }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadData();
        this.notificationCheckService.loadData();
      }
    });
  }

  viewDoc(doc: DropBoxDocument) {
    this.actionService.viewDoc(doc);
  }
}
